/**
 * Everything a self user can connect, for the welcome page: RiskLo's direct
 * connections and the most popular brokerages linked through SnapTrade, in
 * one list with logos and connected state.
 *
 * action.type:
 *   'page'      go to a RiskLo page (direct brokers open their setup there)
 *   'snaptrade' open SnapTrade's sign-in for that brokerage (slug)
 */

const ST_LOGO = (file) => `https://passiv-brokerage-logos.s3.ca-central-1.amazonaws.com/${file}`;

// Popular SnapTrade brokerages. Logos come from SnapTrade's live list when we
// have it; these are the fallbacks (e.g. for free users, who can't load it).
const POPULAR_LINKED = [
  { slug: 'ROBINHOOD', title: 'Robinhood', blurb: 'Stocks, options and crypto.', logo: ST_LOGO('robinhood-logo-square.png'), hue: 'green' },
  { slug: 'SCHWAB', title: 'Charles Schwab', blurb: 'Brokerage and IRAs, including thinkorswim.', logo: ST_LOGO('schwab-logo-square.png'), hue: 'blue' },
  { slug: 'WEBULL', title: 'Webull', blurb: 'Stocks, ETFs and options.', logo: ST_LOGO('webull-logo-square.jpg'), hue: 'blue' },
  { slug: 'FIDELITY', title: 'Fidelity', blurb: 'Brokerage, IRAs and 401(k).', logo: ST_LOGO('fidelity-logo-square.png'), hue: 'green' },
  { slug: 'ETRADE', title: 'E*Trade', blurb: 'Stocks, options and futures.', logo: ST_LOGO('etrade-logo-square.jpg'), hue: 'violet' },
  { slug: 'VANGUARD', title: 'Vanguard', blurb: 'Funds, ETFs and retirement accounts.', logo: ST_LOGO('vanguard-logo-square.png'), hue: 'red' },
];

const DIRECT = [
  { id: 'ninjatrader', title: 'NinjaTrader', blurb: 'Tradovate futures accounts: equity, trades and daily P&L.', logo: '/brokers/ninjatrader.jpg', hue: 'orange', page: 'client-reporting?connect=ninjatrader' },
  { id: 'tradeWatcher', title: 'Live NinjaTrader alerts', blurb: 'The RiskLo Tally add-on: live P&L and naked-position alerts.', mark: '⚡', hue: 'violet', page: 'trade-watcher' },
  { id: 'coinbase', title: 'Coinbase', blurb: 'Crypto holdings, trades and portfolio value.', logo: '/brokers/coinbase.webp', hue: 'blue', page: 'client-reporting?connect=coinbase' },
  { id: 'ibkr', title: 'Interactive Brokers', blurb: 'Read-only Flex report.', logo: '/brokers/ibkr.png', hue: 'red', page: 'client-reporting?connect=ibkr' },
  { id: 'alpaca', title: 'Alpaca', blurb: 'Stocks and options: balance, positions and trades.', logo: '/brokers/alpaca.png', hue: 'yellow', page: 'client-reporting?connect=alpaca' },
  { id: 'csv', title: 'Import a CSV', blurb: 'Have a broker export? Upload it.', mark: 'CSV', hue: 'teal', page: 'client-reporting?connect=all' },
];

// Display order before "connected first".
const ORDER = ['ROBINHOOD', 'SCHWAB', 'ninjatrader', 'tradeWatcher', 'WEBULL', 'FIDELITY', 'coinbase', 'ibkr', 'alpaca', 'ETRADE', 'VANGUARD', 'csv'];

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** True when a linked institution name ("Schwab", "Robinhood") is this brokerage. */
function isLinked(linked, title, slug) {
  const t = norm(title);
  const sl = norm(slug);
  return linked.some((n) => n && (t.includes(n) || n.includes(sl) || sl.includes(n)));
}

/** The popular SnapTrade brokerages, for "Coming soon" tiles while SnapTrade isn't live. */
export const COMING_SOON_BROKERS = POPULAR_LINKED.map(({ slug, title, logo }) => ({ slug, title, logo }));

/**
 * @param conn        getMyConnections() result (or null)
 * @param brokerages  SnapTrade's live list (or null): [{ slug, name, logo }]
 * @param snaptradeAvailable  false when the server has no SnapTrade key: those
 *   brokerages still show, marked "Coming soon". true or null (not known) = linkable.
 */
export function buildConnectItems(conn, brokerages = null, { snaptradeAvailable = null } = {}) {
  const linked = (conn?.linkedInstitutions || []).map(norm);
  const live = new Map((brokerages || []).map((b) => [b.slug, b]));
  // Only when the server says it has no SnapTrade key, never guessed.
  const comingSoon = snaptradeAvailable === false;

  const items = [
    ...DIRECT.map((d) => ({
      key: d.id,
      title: d.title,
      blurb: d.blurb,
      logo: d.logo || null,
      mark: d.mark || d.title.charAt(0),
      hue: d.hue,
      connected: Boolean(conn && conn[d.id]),
      action: { type: 'page', page: d.page },
    })),
    ...POPULAR_LINKED.map((p) => {
      const connected = isLinked(linked, p.title, p.slug);
      return {
        key: p.slug,
        title: p.title,
        blurb: `${p.blurb} Read-only.`,
        logo: live.get(p.slug)?.logo || p.logo,
        mark: p.title.charAt(0),
        hue: p.hue,
        connected,
        comingSoon: comingSoon && !connected,
        action: { type: 'snaptrade', slug: p.slug },
      };
    }),
  ];

  // Linked brokerages outside the popular list still show as connected.
  (conn?.linkedInstitutions || []).forEach((name) => {
    if (items.some((it) => it.connected && isLinked([norm(name)], it.title, it.key))) return;
    items.push({
      key: `linked-${norm(name)}`,
      title: name,
      blurb: 'Linked brokerage.',
      logo: [...live.values()].find((b) => isLinked([norm(name)], b.name, b.slug))?.logo || null,
      mark: name.charAt(0),
      hue: 'indigo',
      connected: true,
      action: { type: 'page', page: 'client-reporting?connect=snaptrade' },
    });
  });

  const rank = (it) => {
    const i = ORDER.indexOf(it.key);
    return i === -1 ? 99 : i;
  };
  // Connected first, then what can be connected today, then "Coming soon".
  return items.sort((a, b) => (Number(b.connected) - Number(a.connected))
    || (Number(Boolean(a.comingSoon)) - Number(Boolean(b.comingSoon)))
    || (rank(a) - rank(b)));
}

/** How many brokerages there are in total (direct + SnapTrade), for "All N brokers". */
export function totalBrokerCount(brokerages) {
  const linkable = Array.isArray(brokerages) && brokerages.length ? brokerages.length : 39;
  return linkable + 4;
}
